import { string, z } from 'zod'
import { SetStateAction, useCallback, useMemo, useState } from 'react'
import { Section, TBody, Table, THead, TR } from '@expo/html-elements'
import { useTranslation } from 'react-i18next'
import { H2, TD, TH, Text } from '../../components/TextComponents'

import { trpc } from '../../../App'
import { View } from 'react-native'
import { Card, CardBody, CardHeader } from '../../components/Card'
import { Col, Row } from '../../components/Row'
import { dsv } from '../../styles/defaults'

import { useNewAutoForm } from '../../components/form/AutoformV2'
import { RequiredFieldsForm, RequiredFieldsFormFields } from '../../forms/requiredFields'
import { addPrefix } from '../../locales/translationKeys'

import { LoadingScreen } from '../../components/LoadingScreen'

import { ProcessTypeSchema } from '@dpa/common/dist'
import { InputSelect } from '../../components/form/AutoFormInputFields/InputSelect'
import { useAutoFormV2JSXGroups } from '../../hooks/form/useAutoFormV2JSXGroups'
import { datenblattFromSchema } from '../../forms/datenblattFromSchema'
import { useAuthContext } from '../../hooks/context/useAuthContext'
import ProcessUebergabeStepSchema, { ProcessUebergabeStepType } from '@dpa/common/dist/prismaGenerated/inputTypeSchemas/ProcessUebergabeStepSchema'
import { GpjButton } from './projects/components/GpjButton'
import { buildRequiredFieldsExcelBlob, downloadBlob } from '../../utils/requiredFieldsExcel'
// TODO: fixme
const projectArten = [
  'Elektifizierung_AC',
  'Elektifizierung_DC',
  'komplexe_DC_inclIBN',
  'standart_DC_inclIBN',
  'auslieferung_DC',
  'ac_inc_config',
] as const

const hiddenUebergabeSteps = new Set<ProcessUebergabeStepType>(['CompletionOfPlanning2Vertrieb'])
const visibleUebergabeSteps = Object.values(ProcessUebergabeStepSchema.enum).filter(
  (step) => !hiddenUebergabeSteps.has(step)
)

function useStuff(
  processType: z.infer<typeof ProcessTypeSchema>,
  processGateData: ReturnType<typeof trpc.processGate.byProcessType.useQuery>['data']
) {
  const steps = visibleUebergabeSteps
  const results: Partial<Record<ProcessUebergabeStepType, any>> = {}

  const { mutate } = trpc.processGate.update.useMutation()

  for(const step of steps) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {inputFieldEntries, hasData} = useNewAutoForm(
      RequiredFieldsForm,
      RequiredFieldsFormFields,
      (field, value) =>
        mutate({
          field,
          value,
          processType,
          processUebergabeStep: step,
        }),
      processGateData?.[step],
      true
    )

    results[step] = { 
      inputFieldEntries,
      hasData,
      // eslint-disable-next-line react-hooks/rules-of-hooks
      group: useAutoFormV2JSXGroups(inputFieldEntries, datenblattFromSchema),
    }
  }

  return results
}

export function SetRequiredFields() {
  const { t } = useTranslation()

  const { authCtx } = useAuthContext()
  const isAllowed = useMemo(() => authCtx?.roles.includes('Admin'), [authCtx?.roles])

  const [processType, setProcessType] = useState<z.infer<typeof ProcessTypeSchema>>(projectArten[0])

  const processGates = trpc.processGate.byProcessType.useQuery({ processType: processType })

  const downloadRequiredFieldsExcel = useCallback(() => {
    if (!processGates.data) {
      return
    }

    const blob = buildRequiredFieldsExcelBlob(
      processGates.data,
      processType,
      (fieldName) => t(addPrefix('labels', fieldName)),
      (stepName) => t(`uebergabeTitle_${stepName}`),
      (value) => String(value ?? 'Option auswählen'),
      uebergabeSteps
    )

    downloadBlob(blob, `required-fields-${processType}.xlsx`)
  }, [processGates.data, processType, t])

  const stuff = useStuff(processType, processGates.data)
  const group_Vertrieb2PE = stuff.Vertrieb2PE?.group ?? []

  const uebergabeSteps = visibleUebergabeSteps

  const everythingHasData = useMemo(() =>
    Object.values(stuff).every(s => s.hasData)
  , [stuff])

  return (
    <Section>
      <Row>
        <Col cols={12}>
          <Card>
            <CardHeader>
              <Text style={dsv.text}>Uebergabe Felder Festlegen</Text>
            </CardHeader>
            <CardBody customStyle={{ minHeight: 300 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <View style={{ flex: 1 }}>
                  <InputSelect
                    options={projectArten as unknown as string[]}
                    strValue={processType}
                    setStrValue={setProcessType}
                    saveStrValue={setProcessType}
                  />
                </View>
                <GpjButton
                  text="Excel herunterladen"
                  width={210}
                  onPress={downloadRequiredFieldsExcel}
                  isDisabled={processGates.isLoading || !processGates.data}
                />
              </View>
              {processGates.isLoading || !everythingHasData ?
                (
                  <LoadingScreen active />
                ) : !isAllowed ? (
                  <View>
                    <Text>Fehlende Benutzer Berechtigungen, wenden sie sich an einen Administrator</Text>
                  </View>
                ) : (
                  <View>
                    {group_Vertrieb2PE.map(([groupName, groupEntries], groupIdx) => (
                      <table
                        key={groupName}
                        style={{
                          textAlign: 'left',
                          marginBottom: '20px',
                          tableLayout: 'fixed',
                          width: '100%',
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: "200px"}}>
                              <H2 style={{ color: dsv.colors.connectGrau7 }}>{groupName}</H2>
                            </th>
                            {uebergabeSteps.map((step) => (
                              <th key={step}  style={{ color: dsv.colors.connectGrau7 }}>
                                {t(`uebergabeTitle_${step}`)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groupEntries.map(([fieldName], fieldIdx) => (
                            <tr
                              key={fieldName}
                              style={{
                                backgroundColor: fieldIdx % 2 === 0 ? dsv.colors.base : dsv.colors.base2,
                              }}
                            >
                              <th scope="row">{t(addPrefix('labels', fieldName))}</th>
                              {uebergabeSteps.map((step) => (
                                <td key={step}>{stuff[step].group[groupIdx][1][fieldIdx][1]}</td>
                              ))}
                            </tr>
                          )
                          )}
                        </tbody>
                      </table>
                    ))}
                  </View>
                )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Section>
  )
}
